import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { GateScanResult } from '../../types/event';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';

type ScanBanner = {
  tone: 'success' | 'warning' | 'error';
  title: string;
  detail?: string;
};

function resultBanner(result: GateScanResult): ScanBanner {
  if (result.kind === 'spectator_ticket') {
    switch (result.result) {
      case 'ok':
        return { tone: 'success', title: 'Spectator ticket accepted' };
      case 'duplicate':
        return { tone: 'warning', title: 'Ticket already scanned', detail: 'This ticket was used earlier.' };
      case 'refunded':
        return { tone: 'error', title: 'Ticket refunded' };
      case 'cancelled':
      case 'revoked':
        return { tone: 'error', title: 'Ticket cancelled' };
      default:
        return { tone: 'error', title: 'Ticket not recognized' };
    }
  }
  switch (result.result) {
    case 'ok':
      return { tone: 'success', title: 'Vehicle checked in' };
    case 'duplicate':
      return { tone: 'warning', title: 'Already checked in', detail: 'This entry was scanned before.' };
    case 'wrong_event':
      return { tone: 'error', title: 'Wrong event', detail: 'This code belongs to a different event.' };
    case 'revoked':
      return { tone: 'error', title: 'Code revoked', detail: 'Ask the entrant to refresh their entry QR.' };
    case 'not_eligible':
      return { tone: 'error', title: 'Not eligible', detail: 'Entry must be confirmed before check-in.' };
    default:
      return { tone: 'error', title: 'Code not recognized' };
  }
}

const bannerClass: Record<ScanBanner['tone'], string> = {
  success: 'border-emerald-800 bg-emerald-950/40 text-emerald-200',
  warning: 'border-amber-800 bg-amber-950/40 text-amber-200',
  error: 'border-red-900 bg-red-950/40 text-red-200',
};

const GateScanPage: React.FC = () => {
  const { eventId: eventIdParam } = useParams<{ eventId: string }>();
  const eventId = Number(eventIdParam);
  const [token, setToken] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<ScanBanner | null>(null);
  const [eventTitle, setEventTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState<Record<string, number | string> | null>(null);
  const [matches, setMatches] = useState<Record<string, unknown>[]>([]);
  const [networkHint, setNetworkHint] = useState<string | null>(null);

  const refreshTotals = useCallback(
    async (q?: string) => {
      if (!eventId || Number.isNaN(eventId)) return;
      try {
        const data = await apiService.getGateTotals(eventId, q);
        setTotals(data.totals);
        setMatches(data.matches || []);
        setNetworkHint(null);
      } catch {
        setNetworkHint('Could not refresh live totals. Check-in can still continue with manual codes.');
      }
    },
    [eventId]
  );

  useEffect(() => {
    if (!eventId || Number.isNaN(eventId)) return;
    (async () => {
      try {
        const evt = await apiService.getEvent(eventId);
        setEventTitle(evt.title);
        await refreshTotals();
      } catch {
        /* non-blocking */
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, refreshTotals]);

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = token.trim();
    if (!trimmed) return;
    try {
      setBusy(true);
      setBanner(null);
      const result = await apiService.gateScan({ event_id: eventId, token: trimmed, mode: 'manual' });
      setBanner(resultBanner(result));
      if (result.result === 'ok') setToken('');
      await refreshTotals(search.trim() || undefined);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Scan failed';
      setBanner({
        tone: 'error',
        title: 'Scan failed',
        detail: navigator.onLine === false ? 'Network unavailable. Use the printed roster export.' : message,
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageLoader message="Loading gate scanner…" />
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-8">
        <PageBackLink label="Back to Event" to={`/organizer/events/${eventId}`} className="mb-4" />
        <p className="kicker mb-2">Event day</p>
        <h1 className="heading-display mb-1">Gate scan</h1>
        {eventTitle ? <p className="text-muted text-sm mb-4">{eventTitle}</p> : null}

        {totals ? (
          <dl className="grid grid-cols-2 gap-2 mb-6 text-sm">
            <div className="rounded-lg border border-white/[0.08] bg-black/40 p-3">
              <dt className="text-gray-500 text-xs">Vehicles in</dt>
              <dd className="text-lg font-semibold text-white">
                {totals.vehicles_checked_in}/{totals.vehicles_confirmed}
              </dd>
            </div>
            <div className="rounded-lg border border-white/[0.08] bg-black/40 p-3">
              <dt className="text-gray-500 text-xs">Tickets scanned</dt>
              <dd className="text-lg font-semibold text-white">
                {totals.tickets_scanned}/{Number(totals.tickets_valid || 0) + Number(totals.tickets_scanned || 0)}
              </dd>
            </div>
          </dl>
        ) : null}

        {networkHint ? (
          <p className="text-xs text-amber-300 mb-4" role="status">
            {networkHint}
          </p>
        ) : null}

        <form onSubmit={(e) => void handleScan(e)} className="space-y-4 mb-6">
          <div>
            <label htmlFor="gate-token" className="input-label">
              Entry or ticket code
            </label>
            <input
              id="gate-token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="input font-mono text-base min-h-[48px]"
              placeholder="Paste or type the code"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <Button type="submit" variant="primary" loading={busy} fullWidth className="min-h-[48px]">
            Check in
          </Button>
        </form>

        {banner ? (
          <div
            role="status"
            className={`rounded-xl border px-4 py-3 mb-6 ${bannerClass[banner.tone]}`}
          >
            <p className="font-medium">{banner.title}</p>
            {banner.detail ? <p className="text-sm mt-1 opacity-90">{banner.detail}</p> : null}
          </div>
        ) : null}

        <div className="mb-6 space-y-3">
          <label htmlFor="gate-search" className="input-label">
            Search tickets or entries
          </label>
          <div className="flex gap-2">
            <input
              id="gate-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input flex-1 min-h-[44px]"
              placeholder="Last 4, name, or registration id"
            />
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              onClick={() => void refreshTotals(search.trim() || undefined)}
            >
              Search
            </Button>
          </div>
          {matches.length > 0 ? (
            <ul className="text-xs text-gray-400 space-y-1">
              {matches.map((m, idx) => (
                <li key={idx}>
                  {String(m.kind)} · {String(m.status)} · last4 {String(m.token_last4 ?? '—')}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <p className="text-xs text-gray-600 leading-relaxed">
          Manual code entry is supported. Camera scanning is not installed until a scanner dependency is approved.
          Export a printed roster from Event admin if the network drops.
        </p>

        <div className="mt-8 pt-6 border-t border-white/[0.06] space-y-3">
          <button
            type="button"
            className="text-sm text-muted hover:text-white min-h-[44px] inline-flex items-center"
            onClick={() => {
              void apiService.downloadEventDayRoster(eventId, 'both').catch(() => {
                setBanner({ tone: 'error', title: 'Export failed', detail: 'Try again when online.' });
              });
            }}
          >
            Download event-day roster CSV →
          </button>
          <br />
          <Link
            to={`/organizer/events/${eventId}/checkin`}
            className="text-sm text-muted hover:text-white min-h-[44px] inline-flex items-center"
          >
            Open full check-in list →
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default GateScanPage;
